import { useNavigate } from 'react-router-dom';
import { ArrowRight, Cpu, Zap, Clock, TrendingUp, ShieldCheck, Database, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui';
import CountUp from '../components/ui/CountUp';

/* -- Agent roster --------------------------------------------------------- */
const AGENT_UNITS = [
  { emoji: '\ud83c\udfaf', name: 'Chief of Staff',    role: 'Leadership',   color: '#6366f1' },
  { emoji: '\ud83d\udd0d', name: 'Lead Hunter',       role: 'Sales',        color: '#6366f1' },
  { emoji: '\ud83d\udcde', name: 'Outbound Rep',      role: 'Sales',        color: '#6366f1' },
  { emoji: '\u270d\ufe0f', name: 'Content Creator',   role: 'Marketing',    color: '#f59e0b' },
  { emoji: '\ud83d\uded2', name: 'Cart Recovery',     role: 'E-Commerce',   color: '#f59e0b' },
  { emoji: '\ud83d\udcc8', name: 'Trend Spotter',     role: 'Intelligence', color: '#6366f1' },
  { emoji: '\ud83d\udee1\ufe0f', name: 'Ticket Responder',  role: 'Support',      color: '#22c55e' },
  { emoji: '\ud83d\udcb0', name: 'Invoice Chaser',    role: 'Finance',      color: '#f59e0b' },
  { emoji: '\ud83e\udde0', name: 'Win Strategist',    role: 'Intelligence', color: '#6366f1' },
];

/* Static activity widths */
const AGENT_ACTIVITY = [78, 91, 65, 83, 72, 96, 61, 85, 70];

const STATS = [
  { value: '31',   unit: 'Agents',  label: 'AI agents deployed',   color: '#6366f1' },
  { value: '24/7', unit: 'Uptime',  label: 'Always operational',   color: '#22c55e' },
  { value: '10\u00d7',  unit: 'ROI',    label: 'Output multiplier',    color: '#f59e0b' },
  { value: '<5',   unit: 'Mins',    label: 'Time to first deploy', color: '#6366f1' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Initialize Your Workforce',
    body: 'Sign up, tell us about your business, and deploy 31 specialist AI agents in under 5 minutes. No engineering required.',
    icon: '\ud83d\ude80',
    color: '#6366f1',
  },
  {
    step: '02',
    title: 'Agents Go to Work Immediately',
    body: 'Your Lead Hunter finds prospects. Your Content Creator drafts posts. Your Invoice Chaser follows up. All simultaneously, 24/7, while you sleep.',
    icon: '\u26a1',
    color: '#f59e0b',
  },
  {
    step: '03',
    title: 'You Stay in Control',
    body: 'Review what matters in your morning briefing. Approve high-stakes actions. Watch your pipeline grow. You decide how autonomous each agent gets.',
    icon: '\ud83c\udfaf',
    color: '#6366f1',
  },
];

const FEATURE_BLOCKS = [
  {
    icon: Clock,
    title: 'True 24/7 Operation',
    desc: 'Cloudflare Durable Objects \u2014 not cron-triggered. Each agent is always awake, holding state, processing without timeouts or cold starts.',
    color: '#6366f1',
  },
  {
    icon: Database,
    title: 'Persistent Memory',
    desc: 'Agents learn your brand voice, customers, and what converts. Knowledge accumulates across every run via vector embeddings.',
    color: '#22c55e',
  },
  {
    icon: TrendingUp,
    title: 'Measurable ROI',
    desc: 'Every action logged. Every revenue event attributed. See exactly what each agent generated \u2014 leads, revenue, time saved.',
    color: '#f59e0b',
  },
  {
    icon: ShieldCheck,
    title: 'Human-in-the-Loop',
    desc: 'Supervised mode lets you approve high-stakes actions before execution. Full control, full visibility \u2014 never a rogue agent.',
    color: '#6366f1',
  },
];

const PRICING_TIERS = [
  {
    name: 'Free Trial',
    price: 0,
    annualPrice: 0,
    credits: '500',
    color: '#8b8b9e',
    features: [
      'All 31 AI agents unlocked',
      '500 one-time credits',
      'Full feature access',
      'No credit card required',
    ],
    cta: 'Start Free Trial',
    highlight: false,
  },
  {
    name: 'Starter',
    price: 49,
    annualPrice: 39,
    credits: '5,000/mo',
    color: '#6366f1',
    features: [
      'All 31 AI agents',
      '5,000 credits/month',
      '1-month credit rollover',
      '$0.015/credit overage ($75 cap)',
      'Top-up packs available',
    ],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Growth',
    price: 149,
    annualPrice: 119,
    credits: '18,000/mo',
    color: '#22c55e',
    features: [
      'All 31 AI agents',
      '18,000 credits/month',
      '1-month credit rollover',
      '$0.012/credit overage ($150 cap)',
      'Performance watchdog alerts',
      'Campaign pivot proposals',
    ],
    cta: 'Start Growing',
    highlight: true,
  },
  {
    name: 'Scale',
    price: 349,
    annualPrice: 279,
    credits: '50,000/mo',
    color: '#f59e0b',
    features: [
      'All 31 AI agents',
      '50,000 credits/month',
      '1-month credit rollover',
      '$0.010/credit overage ($250 cap)',
      'Performance watchdog alerts',
      'Campaign pivot proposals',
    ],
    cta: 'Scale Up',
    highlight: false,
  },
  {
    name: 'Pro',
    price: 699,
    annualPrice: 579,
    credits: '120,000/mo',
    color: '#f59e0b',
    features: [
      'All 31 AI agents',
      '120,000 credits/month (150K fair-use)',
      '$0.009/credit overage ($400 cap)',
      'Priority support',
      'Performance watchdog + auto-pivot',
    ],
    cta: 'Go Pro',
    highlight: false,
  },
  {
    name: 'Enterprise',
    price: 1499,
    annualPrice: 1249,
    credits: '300,000/mo',
    color: '#6366f1',
    features: [
      '300,000 credits (fair-use)',
      'All 31 AI agents',
      'Priority 24/7 support',
      'Dedicated success manager',
      'Custom integrations + API',
      'Contact sales for overage',
    ],
    cta: 'Contact Sales',
    highlight: false,
  },
];

const FAQ_ITEMS = [
  {
    q: 'What exactly do these agents do?',
    a: "Each agent is a specialist. The Lead Hunter finds and qualifies B2B prospects 24/7 using your ICP. The Content Creator drafts blog posts, emails, and social content in your voice. The Invoice Chaser follows up on unpaid invoices automatically. They all work simultaneously \u2014 like having 31 employees who never sleep.",
  },
  {
    q: 'How is this different from ChatGPT or other AI tools?',
    a: "ChatGPT requires you to do the prompting. CoordinAGENTS agents run autonomously on schedules \u2014 finding leads, drafting content, monitoring competitors, recovering carts \u2014 without you lifting a finger. They also remember everything (persistent memory), coordinate with each other (signal bus), and learn your business over time.",
  },
  {
    q: 'Do I need to be technical to set this up?',
    a: "Zero technical knowledge required. You answer 4 questions during onboarding (business name, industry, website, description) and your entire AI workforce is deployed instantly. No API keys to configure, no prompts to write, no infrastructure to manage.",
  },
  {
    q: 'Can I control what the agents do before they act?',
    a: "Yes. Every agent supports three control modes: Guided (you approve everything first), Supervised (agents act but you review results), and Autonomous (full delegation). You set this per-agent. High-stakes actions like sending emails or posting content default to asking for approval.",
  },
  {
    q: 'What integrations do you support?',
    a: "Out of the box: Slack (approvals + alerts in your workspace), Shopify (full e-commerce automation), Twilio (SMS outreach), Firecrawl (web research), Stripe (billing). More integrations are added every month. Agents can also browse the web and write emails via Resend.",
  },
  {
    q: 'How do the agents learn about my business?',
    a: "Agents build a model of your business in three ways: (1) you tell them during onboarding, (2) they learn from every interaction you have with them \u2014 your communication style, what actions you approve/reject, your writing examples \u2014 and (3) they share business intelligence with each other via the cross-team signal bus.",
  },
  {
    q: 'What happens to my data?',
    a: "All data is stored in isolated Cloudflare Durable Objects scoped to your account \u2014 completely separate from other users. Agent memory is stored in Cloudflare Vectorize (vector database) and D1 (SQL) with your user ID as a filter on every query. We never train on your data.",
  },
];

const SOCIAL_PROOF = [
  { end: 2.3,   decimals: 1, prefix: '$', suffix: 'M', label: 'Revenue attributed to agents',    color: '#6366f1' },
  { end: 14200, decimals: 0, suffix: '+',              label: 'Leads discovered automatically',  color: '#22c55e', separator: ',' },
  { end: 98.2,  decimals: 1,             suffix: '%',  label: 'Avg agent uptime',                color: '#f59e0b' },
  { end: 8.4,   decimals: 1,             suffix: '\u00d7',  label: 'Avg actions vs. human baseline',  color: '#6366f1' },
];

/* -- FAQ Item ------------------------------------------------------------- */
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-800 bg-gray-900/60 rounded-lg transition-colors hover:border-gray-700">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left gap-4"
      >
        <span className="text-sm font-semibold text-white">{q}</span>
        {open
          ? <ChevronUp size={16} className="text-indigo-400 shrink-0" />
          : <ChevronDown size={16} className="text-gray-500 shrink-0" />
        }
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-gray-800 animate-fade-slide-up">
          <p className="text-sm text-gray-400 leading-relaxed pt-3">{a}</p>
        </div>
      )}
    </div>
  );
}

/* -- Feature card --------------------------------------------------------- */
function FeatureCard({ f }: { f: typeof FEATURE_BLOCKS[0] }) {
  const Icon = f.icon;

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-6 relative group cursor-default hover:border-gray-700 transition-all duration-300">
      {/* Top accent gradient */}
      <div
        className="absolute top-0 left-0 right-0 h-px rounded-t-lg transition-opacity group-hover:opacity-100"
        style={{ background: `linear-gradient(90deg, ${f.color}, transparent)`, opacity: 0.4 }}
      />

      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg border transition-all group-hover:scale-105"
          style={{
            background: f.color + '12',
            borderColor: f.color + '30',
            color: f.color,
          }}
        >
          <Icon size={20} />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm text-white mb-2 group-hover:text-indigo-300 transition-colors">
            {f.title}
          </p>
          <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
        </div>
      </div>
    </div>
  );
}

/* -- Agent card ----------------------------------------------------------- */
function AgentCard({ agent, activity }: { agent: typeof AGENT_UNITS[0]; activity: number }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="bg-gray-900/60 border border-gray-800 rounded-lg group cursor-pointer transition-all duration-300 hover:border-gray-700"
      style={{
        borderColor: hovered ? agent.color + '40' : undefined,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="p-5">
        {/* Top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-px rounded-t-lg transition-opacity duration-300"
          style={{ background: `linear-gradient(90deg, ${agent.color}, transparent)`, opacity: hovered ? 0.6 : 0.2 }}
        />

        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center text-xl rounded-lg border bg-gray-800/60 transition-all duration-300"
              style={{
                borderColor: hovered ? agent.color + '50' : 'rgb(55 65 81)',
              }}
            >
              {agent.emoji}
            </div>
            <div>
              <p className="text-sm font-semibold text-white transition-colors" style={{ color: hovered ? agent.color : undefined }}>{agent.name}</p>
              <p className="text-xs text-gray-500">{agent.role}</p>
            </div>
          </div>
          <div
            className="w-2 h-2 rounded-full mt-1"
            style={{ background: agent.color }}
          />
        </div>

        <div className="flex items-center justify-between mb-3">
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-md border"
            style={{ borderColor: agent.color + '30', background: agent.color + '10', color: agent.color }}
          >
            {agent.role}
          </span>
          <span className="text-xs font-medium" style={{ color: agent.color }}>Active</span>
        </div>

        {/* Activity bar */}
        <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000"
            style={{
              width: `${activity}%`,
              background: `linear-gradient(90deg, ${agent.color}, ${agent.color}80)`,
            }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-xs text-gray-500">Load</span>
          <span className="text-xs font-medium" style={{ color: agent.color }}>{activity}%</span>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden">

      {/* ================================================================
          NAVIGATION
          ================================================================ */}
      <nav className="border-b border-gray-800 bg-gray-950/90 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div>
              <span className="text-base font-bold text-white">
                CoordinAgents
              </span>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-xs text-gray-500">Online</span>
              </div>
            </div>
          </div>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-6">
            {[
              { label: 'How It Works', id: 'how-it-works' },
              { label: 'Agents', id: 'agents' },
              { label: 'Pricing', id: 'pricing' },
              { label: 'FAQ', id: 'faq' },
            ].map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-sm text-gray-400 hover:text-white transition-colors"
              >
                {label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
              Sign In
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/login')}>
              Deploy Free <ArrowRight size={11} />
            </Button>
          </div>
        </div>
      </nav>

      <main>
      {/* ================================================================
          HERO
          ================================================================ */}
      <section className="relative overflow-hidden">
        {/* Subtle gradient background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(99,102,241,0.12) 0%, transparent 70%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left -- text */}
            <div className="animate-fade-slide-up">
              <div className="inline-flex items-center gap-2 border border-indigo-500/30 bg-indigo-500/10 rounded-full px-4 py-1.5 mb-8">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                <span className="text-xs font-medium text-indigo-300">
                  31 AI Agents Ready to Deploy
                </span>
              </div>

              <h1 className="font-bold leading-tight mb-6">
                <span className="block text-4xl sm:text-5xl lg:text-6xl text-white">
                  Your AI
                </span>
                <span
                  className="block text-5xl sm:text-6xl lg:text-7xl"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #a78bfa 50%, #22c55e 100%)',
                    backgroundSize: '200% 200%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Workforce
                </span>
                <span className="block text-2xl sm:text-3xl text-gray-400 mt-1 font-medium">
                  Never Sleeps
                </span>
              </h1>

              <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-md">
                Deploy 31 autonomous AI agents that handle sales, marketing, support, finance,
                and e-commerce -- all coordinated, all learning, all running 24/7 without you.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <Button size="lg" onClick={() => navigate('/login')}>
                  <Zap size={14} />
                  Get Started Free
                  <ArrowRight size={14} />
                </Button>
                <Button variant="secondary" size="lg" onClick={() => scrollTo('how-it-works')}>
                  See How It Works
                </Button>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                {['No credit card required', 'Deploy in 5 minutes', 'Cancel anytime'].map(t => (
                  <div key={t} className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs text-gray-400">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right -- agent preview panel */}
            <div className="hidden lg:block">
              <div className="bg-gray-900/80 border border-gray-800 rounded-lg overflow-hidden">
                {/* Panel top bar */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-800 bg-gray-900">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500/60" />
                    <div className="w-2 h-2 rounded-full bg-yellow-500/60" />
                    <div className="w-2 h-2 rounded-full bg-green-500/60" />
                  </div>
                  <span className="flex-1 text-center text-xs text-gray-500">CoordinAgents Dashboard</span>
                </div>

                {/* Panel body */}
                <div className="p-6 space-y-3 font-mono text-sm" style={{ minHeight: 340 }}>
                  <p className="text-green-400">$ coordinagents status --all</p>
                  <div className="space-y-2 pl-2">
                    {AGENT_UNITS.slice(0, 7).map((u, i) => (
                      <div
                        key={u.name}
                        className="flex items-center gap-3 animate-fade-slide-up"
                        style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both' }}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: u.color }}
                        />
                        <span className="text-gray-300">{u.name}</span>
                        <span className="ml-auto text-xs" style={{ color: u.color }}>Active</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 text-gray-600">
                      <span>...</span>
                      <span>+24 more agents</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-800">
                    <p className="text-indigo-400">
                      <span className="text-gray-600">$</span> 31/31 agents operational
                      <span className="inline-block w-2 h-4 bg-indigo-400 animate-blink ml-0.5 align-middle" />
                    </p>
                  </div>
                </div>

                <div className="px-4 py-2.5 border-t border-gray-800 bg-gray-900 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs text-green-400">All systems operational</span>
                  </div>
                  <span className="text-xs text-gray-600">coordinagents.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          STATS
          ================================================================ */}
      <section className="border-y border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-800">
            {STATS.map(({ value, unit, label, color }) => (
              <div key={unit} className="py-10 text-center group relative overflow-hidden">
                <div className="text-xs font-medium text-gray-500 mb-2">{unit}</div>
                <p
                  className="font-bold text-4xl sm:text-5xl lg:text-6xl transition-all duration-300"
                  style={{ color }}
                >
                  {value}
                </p>
                <p className="text-gray-400 text-sm mt-2">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          HOW IT WORKS
          ================================================================ */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">How It Works</p>
            <h2 className="font-bold text-3xl sm:text-4xl text-white mb-3">
              Up and Running in 5 Minutes
            </h2>
            <p className="text-gray-400 text-base max-w-md mx-auto">
              No engineers. No prompt engineering. No infrastructure.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={step.step} className="relative">
                {/* Connector line */}
                {i < HOW_IT_WORKS.length - 1 && (
                  <div
                    className="hidden md:block absolute top-8 left-full z-10 h-px"
                    style={{
                      width: 'calc(100% - 2rem)',
                      background: `linear-gradient(90deg, ${step.color}40, transparent)`,
                    }}
                  />
                )}
                <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-6 h-full relative group overflow-hidden transition-all duration-300 hover:border-gray-700">
                  {/* Top accent */}
                  <div
                    className="absolute top-0 left-0 right-0 h-px rounded-t-lg"
                    style={{ background: `linear-gradient(90deg, ${step.color}60, transparent)` }}
                  />

                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-lg border font-bold text-sm transition-all duration-300 group-hover:scale-105"
                      style={{
                        borderColor: step.color + '40',
                        background: step.color + '12',
                        color: step.color,
                      }}
                    >
                      {step.step}
                    </div>
                    <span className="text-2xl transition-transform duration-300 group-hover:scale-110 inline-block">
                      {step.icon}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA below */}
          <div className="mt-10 text-center">
            <Button size="lg" onClick={() => navigate('/login')}>
              <Zap size={14} /> Start Free -- Deploy in 5 Minutes <ArrowRight size={14} />
            </Button>
          </div>
        </div>
      </section>

      {/* ================================================================
          SOCIAL PROOF -- ANIMATED COUNTERS
          ================================================================ */}
      <section className="py-20 bg-gray-900/50 border-y border-gray-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <p className="text-center text-xs font-medium text-gray-500 mb-12 uppercase tracking-wide">Platform Metrics</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {SOCIAL_PROOF.map(({ end, decimals, prefix, suffix, label, color, separator }) => (
              <div key={label} className="text-center group">
                <p
                  className="font-bold text-3xl sm:text-4xl lg:text-5xl"
                  style={{ color }}
                >
                  <CountUp
                    end={end}
                    decimals={decimals}
                    prefix={prefix ?? ''}
                    suffix={suffix ?? ''}
                    separator={separator ?? ''}
                    duration={2200}
                  />
                </p>
                <p className="text-sm text-gray-500 mt-2 leading-snug">{label}</p>
                {/* Underline on hover */}
                <div
                  className="mx-auto mt-3 h-px w-0 group-hover:w-16 transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          AGENT ROSTER
          ================================================================ */}
      <section id="agents" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">Agent Roster</p>
            <h2 className="font-bold text-3xl sm:text-4xl text-white mb-3">
              Meet Your 31-Agent AI Team
            </h2>
            <p className="text-gray-400 text-base max-w-md mx-auto">
              Each agent is a deep specialist -- not a generic chatbot.
              Trained for their exact role. Coordinated by a Chief of Staff.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {AGENT_UNITS.map((agent, i) => (
              <AgentCard key={agent.name} agent={agent} activity={AGENT_ACTIVITY[i]} />
            ))}

            {/* +22 more card */}
            <div
              className="bg-gray-900/60 border border-gray-800 rounded-lg flex items-center justify-center cursor-pointer group relative overflow-hidden hover:border-gray-700 transition-all duration-300"
              onClick={() => navigate('/login')}
              style={{ minHeight: 140 }}
            >
              <div className="p-5 text-center relative">
                <p className="text-4xl font-bold text-indigo-400 mb-1">
                  +22
                </p>
                <p className="text-xs font-medium text-gray-500">More agents available</p>
                <p className="text-sm text-gray-600 mt-2">Finance, Intelligence, Shopify, Support</p>
                <Button variant="ghost" size="sm" className="mt-4">
                  View All <ArrowRight size={11} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          SYSTEM FEATURES
          ================================================================ */}
      <section className="py-24 bg-gray-900/50 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">Why We're Different</p>
            <h2 className="font-bold text-3xl sm:text-4xl text-white mb-3">
              Why CoordinAgents is Different
            </h2>
            <p className="text-base text-gray-400 max-w-lg mx-auto">
              Not a wrapper. Not a chatbot. A coordinated autonomous workforce built on Cloudflare's global network.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {FEATURE_BLOCKS.map((f) => (
              <FeatureCard key={f.title} f={f} />
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          PRICING
          ================================================================ */}
      <section id="pricing" className="py-24 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
          <div className="text-center mb-14">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">Pricing</p>
            <h2 className="font-bold text-3xl sm:text-4xl text-white mb-3">
              Choose Your Plan
            </h2>
            <p className="text-gray-400 text-base mb-6">Scale your AI workforce as revenue grows</p>

            {/* Annual toggle */}
            <div className="inline-flex items-center gap-3 border border-gray-800 bg-gray-900 rounded-lg px-4 py-2">
              <span className={`text-sm font-medium transition-colors ${!annual ? 'text-indigo-400' : 'text-gray-500'}`}>Monthly</span>
              <button
                onClick={() => setAnnual(!annual)}
                className={`relative w-10 h-5 rounded-full border transition-colors ${annual ? 'border-indigo-500 bg-indigo-500/20' : 'border-gray-700 bg-gray-800'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-indigo-400 transition-all ${annual ? 'left-5' : 'left-0.5'}`} />
              </button>
              <span className={`text-sm font-medium transition-colors ${annual ? 'text-indigo-400' : 'text-gray-500'}`}>
                Annual <span className="text-green-400">Save 20%</span>
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 overflow-x-auto">
            {PRICING_TIERS.map((tier) => {
              const tc = tier.color;
              const displayPrice = annual ? tier.annualPrice : tier.price;

              return (
                <div
                  key={tier.name}
                  className={`bg-gray-900/60 border rounded-lg relative flex flex-col group overflow-hidden transition-all duration-300 min-w-[200px] ${
                    tier.highlight ? 'border-green-500/50 ring-1 ring-green-500/20' : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <span
                        className="text-xs font-medium px-3 py-1 rounded-full border"
                        style={{ borderColor: tc, background: tc + '20', color: tc }}
                      >
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="p-5 flex-1 relative">
                    <div className="mb-3">
                      <p className="font-bold text-base" style={{ color: tc }}>{tier.name}</p>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-baseline gap-1">
                        <span className="font-bold text-3xl text-white">
                          {displayPrice === 0 ? 'FREE' : `$${displayPrice}`}
                        </span>
                        {displayPrice > 0 && (
                          <span className="text-gray-500 text-sm">/mo</span>
                        )}
                      </div>
                      <p className="text-sm mt-1" style={{ color: tc + 'CC' }}>
                        {tier.credits} credits
                      </p>
                    </div>

                    <ul className="space-y-2 mb-5">
                      {tier.features.map(f => (
                        <li key={f} className="flex items-start gap-2 text-sm text-gray-400 leading-snug">
                          <div className="w-1 h-1 rounded-full shrink-0 mt-2" style={{ background: tc }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-5 pt-0 relative">
                    <Button
                      variant={tier.highlight ? 'primary' : 'secondary'}
                      className="w-full"
                      onClick={() => navigate('/login')}
                    >
                      {tier.cta} <ArrowRight size={12} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            All tiers include all 31 AI agents. 14-day free trial, no credit card required. Annual billing saves ~20%.
          </p>
        </div>
      </section>

      {/* ================================================================
          ROI COMPARISON
          ================================================================ */}
      <section className="py-20 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-medium text-amber-400 uppercase tracking-wide">Cost Analysis</p>
            <h2 className="font-bold text-xl sm:text-2xl text-white mt-3">
              Replace an army. Keep the budget.
            </h2>
            <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
              The math is simple: 31 AI agents cost less per month than one intern per week.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Human cost card */}
            <div className="bg-gray-900/60 border border-red-500/20 rounded-lg p-5 sm:p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-red-500/60 to-transparent rounded-t-lg" />
              <span className="text-xs font-medium text-red-400">Hiring Humans</span>
              <p className="font-bold text-2xl sm:text-3xl text-red-400 mt-3">
                $180K+<span className="text-sm text-gray-500">/yr</span>
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-gray-400 leading-relaxed">
                <li className="flex gap-2"><span className="text-red-400 shrink-0">\u00d7</span> 1 SDR ($60K) + 1 writer ($55K) + 1 support rep ($45K)</li>
                <li className="flex gap-2"><span className="text-red-400 shrink-0">\u00d7</span> Works 8 hours/day, 5 days/week</li>
                <li className="flex gap-2"><span className="text-red-400 shrink-0">\u00d7</span> Benefits, onboarding, management overhead</li>
                <li className="flex gap-2"><span className="text-red-400 shrink-0">\u00d7</span> 2x output = 2x headcount = 2x cost</li>
              </ul>
            </div>

            {/* CoordinAGENTS card */}
            <div className="bg-gray-900/60 border border-green-500/20 rounded-lg p-5 sm:p-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-green-500/60 to-transparent rounded-t-lg" />
              <span className="text-xs font-medium text-green-400">CoordinAgents</span>
              <p className="font-bold text-2xl sm:text-3xl text-green-400 mt-3">
                $149<span className="text-sm text-gray-500">/mo</span>
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-gray-400 leading-relaxed">
                <li className="flex gap-2"><span className="text-green-400 shrink-0">\u2713</span> 31 agents -- sales, marketing, support, finance, intel, e-com</li>
                <li className="flex gap-2"><span className="text-green-400 shrink-0">\u2713</span> Works 24/7/365 -- no breaks, no vacations, no sick days</li>
                <li className="flex gap-2"><span className="text-green-400 shrink-0">\u2713</span> Zero HR overhead -- deployed in 5 minutes</li>
                <li className="flex gap-2"><span className="text-green-400 shrink-0">\u2713</span> Scales instantly -- same price, more throughput</li>
              </ul>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            That's <span className="text-green-400">$1,788/yr</span> vs. <span className="text-red-400">$180,000+/yr</span> -- a 99% cost reduction with 24/7 coverage.
          </p>
        </div>
      </section>

      {/* ================================================================
          FAQ
          ================================================================ */}
      <section id="faq" className="py-24 bg-gray-900/50 border-t border-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">FAQ</p>
            <h2 className="font-bold text-3xl sm:text-4xl text-white mb-3">
              Common Questions
            </h2>
            <p className="text-gray-400 text-base">Everything you need to know before deploying your first agent.</p>
          </div>

          <div className="space-y-2">
            {FAQ_ITEMS.map(item => (
              <FaqItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================
          CTA / FINAL
          ================================================================ */}
      <section className="py-24 border-t border-gray-800 relative overflow-hidden">
        {/* Subtle gradient */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 80% at 50% 50%, rgba(99,102,241,0.08) 0%, transparent 70%)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex justify-center mb-8">
            <div className="bg-gray-900/80 border border-gray-800 rounded-lg px-6 py-4 inline-flex items-center gap-4">
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-indigo-400" />
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <div className="w-2 h-2 rounded-full bg-amber-400" />
              </div>
              <div>
                <p className="font-bold text-xl text-white">CoordinAgents</p>
                <p className="text-xs text-gray-500 mt-0.5">Ready to deploy</p>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-indigo-400" />
                <div className="w-2 h-2 rounded-full bg-green-400" />
                <div className="w-2 h-2 rounded-full bg-amber-400" />
              </div>
            </div>
          </div>

          <h2 className="font-bold text-3xl sm:text-4xl text-white mb-4">
            Ready to Build Your AI Team?
          </h2>
          <p className="text-gray-400 text-base mb-8 max-w-lg mx-auto leading-relaxed">
            Join businesses already running on autopilot. Deploy your 31-agent workforce in under 5 minutes. No engineers required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate('/login')}>
              <Zap size={15} />
              Get Started Free
              <ArrowRight size={15} />
            </Button>
            <p className="text-sm text-gray-500">No credit card \u00b7 14-day free trial \u00b7 Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* ================================================================
          TRUST & COMPLIANCE BAR
          ================================================================ */}
      <section className="border-t border-gray-800 bg-gray-900 px-4 sm:px-6 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide mb-4">Security &amp; Compliance</p>
            <p className="text-base text-gray-400">
              Enterprise-grade safety built into every agent action.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: '\ud83d\udd12', label: 'CAN-SPAM Compliant', desc: 'Unsubscribe in every email. Opt-out honored within 10 days.', color: '#22c55e' },
              { icon: '\ud83d\udee1\ufe0f', label: 'GDPR Ready',         desc: 'Data isolation per account. No cross-user data mixing. Delete on request.', color: '#6366f1' },
              { icon: '\ud83d\udc64', label: 'Human in the Loop',  desc: 'Every critical action gated behind your approval. You stay in control.', color: '#f59e0b' },
              { icon: '\ud83d\udeab', label: 'Do Not Contact List', desc: 'Opt-outs are permanently blocked across all agents instantly.', color: '#6366f1' },
            ].map(item => (
              <div
                key={item.label}
                className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 text-center group transition-all duration-300 hover:border-gray-700"
              >
                <div className="text-2xl mb-2 transition-transform duration-300 group-hover:scale-110 inline-block">{item.icon}</div>
                <p className="font-semibold text-sm text-white mb-1">{item.label}</p>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {[
              { color: '#22c55e', text: 'Approval gates enabled' },
              { color: '#6366f1', text: 'Data isolated per account' },
              { color: '#22c55e', text: 'Unsubscribe enforced' },
              { color: '#6366f1', text: 'Audit log always on' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: item.color }} />
                <span className="text-xs text-gray-400">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      </main>

      {/* ================================================================
          FOOTER
          ================================================================ */}
      <footer className="border-t border-gray-800 bg-gray-950 px-4 sm:px-6 py-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-800 bg-gray-900">
              <Cpu size={12} className="text-gray-500" />
            </div>
            <span className="text-xs text-gray-500">
              &copy; {new Date().getFullYear()} CoordinAgents. Built on Cloudflare.
            </span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center sm:justify-end">
            {[
              { label: 'Privacy', href: 'https://coordinagents.com/privacy' },
              { label: 'Terms', href: 'https://coordinagents.com/terms' },
              { label: 'Contact', href: 'mailto:hello@coordinagents.com' },
            ].map(l => (
              <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-white transition-colors">{l.label}</a>
            ))}
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span className="text-xs text-green-400">Online</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
